import { useMemo, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";

import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";

import { InspectionTable, type InspectionTableRow } from "./clampQcInspectionTable";
import { clampQcTaggedInspectionRows } from "./clampQcMockData";
import { fetchClampQcHubWorkOrderBundle } from "./clampQcBuildOrdersApi";

type LineClampFilter = "all" | "large" | "small";
type LineResultFilter = "all" | "passed" | "failed";

export function ClampQcReportingWorkOrderLinesPage() {
  const { slug } = useParams<{ slug: string }>();
  const normalizedSlug = slug?.trim() ?? "";

  const bundleQuery = useQuery({
    queryKey: ["clamp-qc", "work-order", normalizedSlug],
    queryFn: () => fetchClampQcHubWorkOrderBundle(normalizedSlug),
    enabled: normalizedSlug !== "",
    staleTime: 30_000,
  });

  const [clampFilter, setClampFilter] = useState<LineClampFilter>("all");
  const [resultFilter, setResultFilter] = useState<LineResultFilter>("all");
  const [search, setSearch] = useState("");

  const bundle = bundleQuery.data ?? null;

  const allLines = useMemo(() => (bundle == null ? [] : clampQcTaggedInspectionRows(bundle)), [bundle]);

  const filteredLines = useMemo(() => {
    let rows: InspectionTableRow[] = [...allLines];
    if (clampFilter !== "all") {
      rows = rows.filter((row) => row.clampSize === clampFilter);
    }
    if (resultFilter !== "all") {
      rows = rows.filter((row) =>
        resultFilter === "passed"
          ? row.result.toLowerCase() === "passed"
          : row.result.toLowerCase() !== "passed",
      );
    }
    const needle = search.trim().toLowerCase();
    if (needle) {
      rows = rows.filter((row) => {
        const parts = [
          String(row.serial),
          String(row.boxSerial),
          row.failReason ?? "",
          row.observations ?? "",
        ];
        return parts.some((part) => part.toLowerCase().includes(needle));
      });
    }
    return rows;
  }, [allLines, clampFilter, resultFilter, search]);

  if (normalizedSlug === "") {
    return <Navigate to="/clamp-qc/reporting" replace />;
  }

  if (bundleQuery.isPending) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center gap-2 text-slate-600">
        <Loader2 aria-hidden className="h-5 w-5 animate-spin" />
        <span className="text-sm">Loading work order…</span>
      </div>
    );
  }

  if (bundleQuery.isError || bundle == null) {
    const message =
      bundleQuery.error instanceof Error ? bundleQuery.error.message : "Could not load this work order.";
    return (
      <div className="space-y-4">
        <p className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">{message}</p>
        <p className="text-sm text-slate-600">
          <Link className="font-medium text-brand-700 hover:text-brand-800" to="/clamp-qc/reporting">
            Back to reporting
          </Link>
        </p>
      </div>
    );
  }

  const context = bundle.context;

  return (
    <div className="space-y-6">
      <header className="space-y-2">
        <h1 className="text-xl font-semibold text-slate-900">Inspection lines</h1>
        <p className="text-sm text-slate-600">
          Lines from the hub work order bundle for this job. When the API returns inspection history, it appears here;
          otherwise the grid is empty and operators still capture rows in the job workspace session.
        </p>
        <p className="text-sm">
          <Link to="/clamp-qc/reporting" className="font-medium text-brand-700 hover:text-brand-800">
            Reporting
          </Link>
          <span className="text-slate-400"> · </span>
          <Link to={`/clamp-qc/jobs/${encodeURIComponent(bundle.slug)}`} className="font-medium text-brand-700 hover:text-brand-800">
            Record inspection
          </Link>
        </p>
        <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-slate-700">
          <span>
            <span className="font-medium text-slate-500">Work order</span>{" "}
            <span className="font-semibold text-slate-900">{context.workOrder}</span>
          </span>
          <span>
            <span className="font-medium text-slate-500">Drawing</span>{" "}
            <span className="font-semibold text-slate-900">{context.drawingRevision}</span>
          </span>
          <span>
            <span className="font-medium text-slate-500">Purchase order</span>{" "}
            <span className="font-semibold text-slate-900">{context.purchaseOrder}</span>
          </span>
        </div>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardBody className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-medium uppercase tracking-wide text-slate-500">Clamp size</span>
            {(["all", "large", "small"] satisfies LineClampFilter[]).map((value) => (
              <Button
                key={value}
                size="sm"
                variant={clampFilter === value ? "primary" : "secondary"}
                type="button"
                onClick={() => setClampFilter(value)}
              >
                {value === "all" ? "All" : value === "large" ? "Large" : "Small"}
              </Button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-medium uppercase tracking-wide text-slate-500">Result</span>
            {(["all", "passed", "failed"] satisfies LineResultFilter[]).map((value) => (
              <Button
                key={value}
                size="sm"
                variant={resultFilter === value ? "primary" : "secondary"}
                type="button"
                onClick={() => setResultFilter(value)}
              >
                {value === "all" ? "All" : value === "passed" ? "Passed" : "Failed"}
              </Button>
            ))}
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600">Search lines</label>
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Serial, carton label, notes…"
              className="mt-1 max-w-xl"
              autoComplete="off"
            />
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>
            {filteredLines.length === allLines.length
              ? `${allLines.length} inspection lines`
              : `${filteredLines.length} of ${allLines.length} inspection lines`}
          </CardTitle>
        </CardHeader>
        <CardBody>
          <InspectionTable rows={filteredLines} showClampSize emptyMessage="No lines match these filters." />
        </CardBody>
      </Card>
    </div>
  );
}
